import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  #lockedScroll = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Dialogs freeze the page by default so the content behind can't scroll
    // out from under them. A dialog can opt out with `allow-page-scroll` when
    // it's meant to sit alongside the page rather than take it over (the
    // search autocomplete anchors under the header and follows it on scroll).
    const lockScroll = !dialog.hasAttribute('allow-page-scroll');
    this.#lockedScroll = lockScroll;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Arrow function so `this` stays the component — a plain declaration here
    // loses it under the module's strict mode.
    const openNow = () => {
      if (lockScroll) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      // A dialog that sits alongside the page opens NON-modally. showModal()
      // puts the dialog in the top layer and makes everything outside it
      // inert, so the header stops receiving pointer events entirely —
      // hovering a megamenu category while the search panel was open did
      // nothing at all, because the hover never reached the nav. Measured:
      // with the dialog modal, document.elementFromPoint() over a nav item
      // returns <html> rather than the item, and a transparent
      // pointer-events:none ::backdrop does not change that.
      //
      // show() leaves the rest of the page live. The panel is already
      // position:fixed with its own top/left/right, so it never needed the
      // top layer to be placed — only a z-index, which Autocomplete.css sets.
      // Escape still closes: #handleKeyDown is bound to the component and
      // focus opens inside the panel, so the keydown bubbles to it.
      if (lockScroll) {
        dialog.showModal();
      } else {
        dialog.show();
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    // A dialog that sits alongside the page has no body writes to batch — the
    // scroll lock is the only reason this was ever deferred to a frame — so it
    // opens synchronously. Waiting on a frame made opening fragile: rAF doesn't
    // run in a hidden document and is deferred in a backgrounded tab, so the
    // click was accepted and the panel simply never appeared. Opening on the
    // click also removes a frame of latency.
    if (!lockScroll) {
      openNow();
      return;
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(openNow);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Only undo the freeze if this dialog applied it. Restoring the scroll
    // position unconditionally would yank an `allow-page-scroll` dialog's page
    // back to wherever it sat when the dialog opened.
    if (this.#lockedScroll) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      this.#lockedScroll = false;
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
